import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Partnerdetail.css";
import "./styles/SophosCloud.css";
import sophosLogo from "../../assets/images/partners/Sophos Cloud Security.png";

function useCounter(target, duration = 2000, inView) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration, inView]);
  return count;
}
function useInView(threshold = 0.3) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

const cloudLayers = [
  { icon: "🌐", name: "Internet / Edge", status: "Active", dotCls: "green" },
  { icon: "🛡️", name: "Sophos Firewall / ZTNA Gateway", status: "Protected", dotCls: "green" },
  { icon: "☁️", name: "Sophos Cloud Optix — Cloud Security Posture", status: "Monitoring", dotCls: "blue" },
  { icon: "💻", name: "Sophos Intercept X — Endpoint", status: "Secured", dotCls: "green" },
  { icon: "📧", name: "Sophos Email Security", status: "Active", dotCls: "green" },
  { icon: "📊", name: "Sophos Central — Management Console", status: "Online", dotCls: "blue" },
];

const archRows = [
  {
    label: "User Layer",
    cards: [
      { icon: "💻", name: "Endpoints", desc: "Intercept X EDR — managed from Sophos Central" },
      { icon: "📱", name: "Mobile Devices", desc: "Sophos Mobile MDM with UEM integration" },
      { icon: "🌐", name: "Remote Users", desc: "ZTNA clientless & client-based access" },
    ],
  },
  {
    label: "Network Layer",
    cards: [
      { icon: "🔥", name: "Sophos Firewall", desc: "XGS Series with synchronized security" },
      { icon: "📧", name: "Email Security", desc: "Inbound/outbound threat protection" },
      { icon: "🌍", name: "Web Gateway", desc: "Cloud web filtering and CASB" },
    ],
  },
  {
    label: "Cloud Layer",
    cards: [
      { icon: "☁️", name: "Cloud Optix", desc: "CSPM for AWS, Azure, Google Cloud" },
      { icon: "🔄", name: "Cloud Workloads", desc: "Linux/Windows server protection" },
      { icon: "🐳", name: "Containers", desc: "Runtime protection and image scanning" },
    ],
  },
  {
    label: "Intelligence",
    cards: [
      { icon: "🧠", name: "SophosAI", desc: "Deep learning threat detection engine" },
      { icon: "🔭", name: "Sophos MDR", desc: "24/7 managed detection and response" },
      { icon: "📊", name: "Sophos Central", desc: "Unified management and analytics" },
    ],
  },
];

const ztPrinciples = [
  { icon: "🔐", title: "Verify Every Identity", desc: "Continuous identity verification for users and devices — no implicit trust based on network location, with adaptive authentication based on risk score and behavioral context." },
  { icon: "📱", title: "Validate Device Posture", desc: "Device health validation before granting access — checking security posture, patch compliance, encryption status, and presence of EDR agent before allowing resource access." },
  { icon: "🎯", title: "Least Privilege Access", desc: "Granular application-level access replacing network-level VPN access — users only access the specific applications they need, reducing lateral movement exposure." },
  { icon: "🔍", title: "Continuous Monitoring", desc: "Ongoing session monitoring and behavioral analysis — detecting anomalies mid-session and automatically revoking access when risk score exceeds policy thresholds." },
];

const endpointStack = [
  { icon: "🛡️", title: "Intercept X with XDR", desc: "Advanced endpoint protection combining signature-less machine learning, behavioral analysis, exploit prevention, and cross-source threat detection in a single unified agent." },
  { icon: "🔗", title: "Synchronized Security", desc: "Sophos Endpoint and Sophos Firewall share security intelligence in real time — if an endpoint detects malware, the firewall automatically isolates it before lateral movement occurs." },
  { icon: "🕵️", title: "Endpoint Detection & Response", desc: "Full EDR capability with process tree visualization, cross-endpoint search, live terminal access, and guided response for deep investigation without requiring analyst expertise." },
  { icon: "🔐", title: "Ransomware Protection", desc: "CryptoGuard technology specifically designed to detect and roll back ransomware encryption — protecting files even when the ransomware strain has never been seen before." },
];

const mdrCards = [
  { icon: "🔭", title: "24/7 Threat Hunting", desc: "Sophos MDR analysts proactively search for threats across your environment — going beyond automated detection to investigate behavioral indicators that fall below alert thresholds." },
  { icon: "⚡", title: "Incident Response", desc: "Full incident response capability as part of the MDR service — Sophos analysts contain, investigate, and remediate active threats without requiring your team to be available." },
  { icon: "📊", title: "Security Operations Reporting", desc: "Weekly and monthly security operations reports covering threat detections, hunting findings, posture improvements, and emerging threat intelligence relevant to your environment." },
  { icon: "🤝", title: "Collaborative Response", desc: "Choose your MDR response mode — Sophos MDR Complete includes full response actions, while MDR Essentials provides managed detection with your team executing response." },
  { icon: "🔗", title: "Third-Party Integration", desc: "Sophos MDR ingests telemetry from non-Sophos security tools — firewall logs, cloud trail events, and identity provider logs — extending detection coverage beyond Sophos products." },
  { icon: "🏆", title: "Outcome Guarantee", desc: "Sophos MDR provides breach warranty coverage — Sophos stands behind MDR's ability to detect active threats through the promise of incident response assistance if a breach occurs." },
];

const aiFeatures = [
  { icon: "🧠", title: "SophosAI Deep Learning", desc: "Sophos's proprietary deep learning engine trained on hundreds of millions of samples — detecting malicious files based on structural characteristics rather than signatures, protecting against novel malware families." },
  { icon: "🔬", title: "Behavioral Analysis Engine", desc: "Runtime behavioral detection continuously monitoring process behavior for malicious activity patterns — catching fileless attacks, living-off-the-land techniques, and script-based threats that bypass signature detection." },
  { icon: "🌐", title: "Cloud-Native Threat Intelligence", desc: "SophosLabs global threat intelligence network providing real-time updates from 500M+ endpoints — correlating global threat campaigns with local environment telemetry for targeted threat detection." },
  { icon: "🤖", title: "Adaptive Attack Protection", desc: "Dynamic security mode that automatically activates the highest protection level when active attacker behavior is detected on an endpoint — temporarily restricting capabilities exploited by attackers during active incidents." },
];

const complianceFrameworks = [
  { icon: "🏥", name: "HIPAA", desc: "PHI protection controls, access logging, and breach notification support for healthcare organizations" },
  { icon: "💳", name: "PCI DSS", desc: "Cardholder data environment protection with anti-malware, network segmentation, and change detection" },
  { icon: "🇺🇸", name: "NIST CSF", desc: "Sophos platform controls mapped to all five NIST CSF functions with evidence collection" },
  { icon: "🔐", name: "SOC 2 Type II", desc: "Continuous monitoring, access control, and incident response evidence for SOC 2 audits" },
  { icon: "🇪🇺", name: "GDPR", desc: "Data protection controls, encryption enforcement, and breach detection for EU compliance" },
  { icon: "🏛️", name: "CMMC 2.0", desc: "Endpoint and network security controls aligned to CMMC 2.0 practice families" },
  { icon: "📋", name: "ISO 27001", desc: "Technical controls mapped to ISO 27001 Annex A with operational documentation" },
  { icon: "🏢", name: "CIS Controls", desc: "CIS Critical Security Controls mapping with implementation guidance and gap analysis" },
];

const industries = [
  { icon: "🏥", name: "Healthcare", desc: "HIPAA-compliant cloud security protecting clinical systems, telehealth platforms, and healthcare IoT with MDR coverage for ransomware threats targeting healthcare." },
  { icon: "🏦", name: "Financial Services", desc: "Multi-layer financial security covering banking endpoints, trading systems, and cloud workloads with PCI DSS and GLBA compliance reporting." },
  { icon: "🏭", name: "Manufacturing", desc: "Endpoint and OT-adjacent security for manufacturing environments — protecting engineering workstations and corporate infrastructure while maintaining OT segmentation." },
  { icon: "🎓", name: "Education", desc: "Cloud security for universities and K-12 organizations — FERPA-compliant endpoint protection, student device management, and research data security." },
  { icon: "⚖️", name: "Legal & Professional", desc: "Privileged document protection, secure client communication, and confidential data isolation for law firms and professional services organizations." },
  { icon: "🏛️", name: "Government", desc: "FedRAMP and FISMA-aligned cloud security for government agencies with CMMC 2.0 compliance for defense contractors." },
  { icon: "🛒", name: "Retail & eCommerce", desc: "POS security, e-commerce platform protection, and distributed store endpoint management with PCI DSS compliance." },
  { icon: "☁️", name: "Technology", desc: "Cloud workload security for SaaS companies and technology firms — container security, DevSecOps integration, and developer endpoint protection." },
];

const faqData = [
  { q: "What is the difference between Sophos Intercept X and Sophos XDR?", a: "Sophos Intercept X is the core endpoint protection product — providing anti-malware, behavioral detection, exploit prevention, application control, device control, and web filtering for managed endpoints. Sophos Intercept X Advanced with XDR adds Extended Detection and Response capabilities on top of the core protection: cross-source telemetry correlation pulling in data from endpoints, firewalls, email, cloud, and mobile; threat hunting tools enabling security analysts to search across the environment for indicators of compromise; process tree visualization reconstructing the attack chain for any detected event; live response terminal access to investigate endpoints remotely; and scheduled queries enabling proactive environmental analysis. For organizations with a security team capable of performing investigation work, Intercept X Advanced with XDR provides the tools they need. For organizations without security analyst staff, Sophos MDR operates the XDR tools on your behalf." },
  { q: "What is Sophos MDR and how does it differ from Sophos XDR?", a: "Sophos XDR is a technology platform — a set of detection, investigation, and hunting tools that your security team or a third-party operates. It provides the capability but requires human expertise to use it effectively. Sophos MDR (Managed Detection and Response) is a fully-managed service where Sophos's expert SOC analysts operate the XDR tools on your behalf 24 hours a day, 7 days a week. MDR analysts proactively hunt for threats, investigate every significant alert, and respond to incidents either collaboratively with your team (MDR Essentials) or with full authority to take containment and remediation actions (MDR Complete). The practical difference: XDR requires your team to have security analyst skills and time availability; MDR eliminates that requirement by providing those experts as a service. ALG Data Guard helps organizations evaluate whether their internal security capability justifies XDR self-operation or whether MDR delivers better security outcomes for their investment." },
  { q: "What is Sophos Synchronized Security and why does it matter?", a: "Sophos Synchronized Security is a technology architecture where Sophos Endpoint agents (Intercept X) and Sophos Firewalls (XGS Series) share real-time security intelligence through the Security Heartbeat — a continuous communication channel between endpoint and firewall. In practice, this creates security automation that improves both detection and response speed: when Intercept X detects a potential threat on an endpoint, it communicates a reduced heartbeat status to the Sophos Firewall — which automatically isolates that endpoint's network access and prevents lateral movement to other systems without requiring an administrator action. Simultaneously, when the firewall detects suspicious traffic associated with an endpoint (like C2 communication), it can alert the endpoint agent for local investigation. This automated, bidirectional intelligence sharing eliminates the manual coordination between endpoint and network security teams that creates response delays in traditional security architectures." },
  { q: "How does Sophos Zero Trust Network Access (ZTNA) replace traditional VPN?", a: "Traditional VPN grants network-level access — once a user authenticates to VPN, they typically have access to the entire network segment, enabling lateral movement if the device is compromised or the user account is stolen. Sophos ZTNA replaces this with application-level access: users authenticate to specific applications rather than gaining broad network access, and access decisions are based on continuous verification of user identity, device health, and behavioral risk rather than a single authentication event at connection time. Device posture is continuously evaluated — Sophos ZTNA checks that the device has the Intercept X agent running, that it passes security posture requirements, and that no behavioral anomalies have been detected. If device health degrades mid-session (e.g., malware is detected), access can be automatically revoked without requiring a separate action. For remote workforce scenarios, ZTNA provides significantly reduced attack surface compared to VPN while improving user experience — users access applications directly through the ZTNA gateway without requiring a full VPN connection first." },
  { q: "How does ALG Data Guard deploy and manage Sophos Cloud environments?", a: "ALG Data Guard provides end-to-end Sophos deployment and managed services. Deployment: Sophos Central tenant configuration aligned to your organizational structure, policy design for endpoint protection balancing security aggressiveness and operational continuity, Intercept X rollout across your endpoint population, Sophos Firewall deployment and configuration for network security and synchronized security integration, ZTNA configuration for remote access, email security configuration, and Cloud Optix setup for cloud workload visibility. Ongoing management: Sophos Central console administration, policy tuning based on detection alerts and operational feedback, monthly security reports covering threat detections and posture metrics, firmware and software update management coordinated within your maintenance windows, MDR coordination (for organizations using Sophos MDR), compliance reporting support, and incident response assistance for significant security events. We also perform quarterly security posture reviews with prioritized improvement recommendations." },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7"/></svg>
);
const ChevronDown = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
);

export default function SophosCloud() {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [metricsRef, metricsInView] = useInView(0.3);
  const c1 = useCounter(500, 2200, metricsInView);
  const c2 = useCounter(400, 2000, metricsInView);
  const c3 = useCounter(150, 2200, metricsInView);
  const c4 = useCounter(24, 2000, metricsInView);

  const threatBars = [
    { label: "Ransomware", pct: "97", cls: "green" },
    { label: "Phishing", pct: "99", cls: "blue" },
    { label: "Zero-Days", pct: "93", cls: "teal" },
    { label: "Supply Chain", pct: "89", cls: "yellow" },
  ];

  return (
    <div className="sc-page pd-page">

      {/* ── Identity Band ── */}
      <div className="pd-identity-band" style={{ background:"#060f26", borderBottom:"1px solid rgba(0,96,255,0.2)" }}>
        <div className="pd-identity-inner">
          <img src={sophosLogo} alt="Sophos" className="pd-partner-logo" />
          <div className="pd-identity-divider" style={{ background:"rgba(255,255,255,0.1)" }} aria-hidden="true" />
          <div className="pd-identity-text">
            <div className="pd-eyebrow" style={{ color:"rgba(255,255,255,0.6)", background:"rgba(0,96,255,0.12)", borderColor:"rgba(0,96,255,0.28)" }}>
              <span className="pd-live-dot" style={{ background:"#00d4ff" }} />
              Cloud Security & Cybersecurity Partner
            </div>
            <h1 className="pd-identity-name" style={{ color:"#fff" }}>ALG Data Guard × Sophos</h1>
            <p className="pd-identity-desc" style={{ color:"rgba(255,255,255,0.65)" }}>
              Sophos is a global leader in cloud-native cybersecurity — delivering endpoint protection, next-generation firewall, email security, zero trust network access, cloud security posture management, and 24/7 managed detection and response through the unified Sophos Central cloud platform. Sophos Synchronized Security is a unique architecture enabling endpoint agents and firewalls to share real-time threat intelligence — creating automated response capabilities unavailable in traditional siloed security tools. ALG Data Guard is an authorized Sophos partner providing design, deployment, and managed security services for the complete Sophos platform.
            </p>
          </div>
        </div>
      </div>

      {/* ── Hero ── */}
      <section className="sc-hero">
        <div className="sc-hero__aurora" aria-hidden="true" />
        <div className="sc-hero__grid" aria-hidden="true" />
        <div className="sc-hero__inner">
          <div>
            <div className="sc-label sc-label--teal"><span className="sc-dot" /> Sophos Authorized Partner</div>
            <h2 className="sc-hero__title">
              Cloud Security<br />Built for the<br />Way You <em>Work Now.</em>
            </h2>
            <p className="sc-hero__body">
              Sophos Central unifies endpoint protection, network security, email security, cloud workload protection, and zero trust access in a single cloud platform — with AI-powered detection and Sophos MDR providing 24/7 expert security operations. ALG Data Guard deploys and manages the complete Sophos platform for organizations across every industry.
            </p>
            <div className="sc-hero__actions">
              <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
                Deploy Sophos Central <ArrowIcon />
              </button>
              <button className="sc-btn sc-btn--glass" onClick={() => navigate("/contact")}>
                Security Assessment
              </button>
            </div>
            <div className="sc-hero__kpis">
              <div><div className="sc-hero__kpi-val">500M+</div><div className="sc-hero__kpi-label">Endpoints Covered</div></div>
              <div><div className="sc-hero__kpi-val">24/7</div><div className="sc-hero__kpi-label">MDR SOC Coverage</div></div>
              <div><div className="sc-hero__kpi-val">99%</div><div className="sc-hero__kpi-label">Threat Block Rate</div></div>
            </div>
          </div>

          {/* Glass dashboard panel */}
          <div className="sc-glass-panel sc-hero-panel">
            <div className="sc-hero-panel__header">
              <span className="sc-hero-panel__title">Sophos Central — Security Dashboard</span>
              <span className="sc-hero-panel__live"><span className="sc-panel-live-dot" /> PROTECTED</span>
            </div>
            <div className="sc-cloud-layers">
              {cloudLayers.map(layer => (
                <div key={layer.name} className="sc-cloud-layer">
                  <span className="sc-cloud-layer__icon">{layer.icon}</span>
                  <span className="sc-cloud-layer__name">{layer.name}</span>
                  <div className="sc-cloud-layer__status">
                    <div className={`sc-cloud-layer__dot sc-cloud-layer__dot--${layer.dotCls}`} />
                    <span className="sc-cloud-layer__status-text">{layer.status}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:"0.9rem", display:"flex", flexDirection:"column", gap:"0.5rem" }}>
              <div style={{ fontSize:"0.65rem", color:"rgba(255,255,255,0.4)", fontFamily:"IBM Plex Mono", letterSpacing:"0.08em", textTransform:"uppercase", marginBottom:"0.3rem" }}>Threat Block Rates</div>
              {threatBars.map(tb => (
                <div key={tb.label} className="sc-threat-bar">
                  <span className="sc-tb-label">{tb.label}</span>
                  <div className="sc-tb-track">
                    <div className={`sc-tb-fill sc-tb-fill--${tb.cls}`} style={{ width:`${tb.pct}%` }} />
                  </div>
                  <span style={{ fontSize:"0.65rem", color:"rgba(255,255,255,0.5)", fontFamily:"IBM Plex Mono", minWidth:"28px", textAlign:"right" }}>{tb.pct}%</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Cloud Architecture ── */}
      <section className="sc-architecture">
        <div className="sc-shell">
          <div className="sc-architecture__head">
            <div className="sc-label sc-label--blue"><span className="sc-dot" /> Cloud Security Platform</div>
            <h2 className="sc-title">Sophos Central — One Platform. Every Layer.</h2>
            <p className="sc-sub" style={{ margin:"0 auto", maxWidth:"620px" }}>Sophos Central provides unified management and synchronized security across every protection layer — from user devices through network perimeter through cloud workloads — eliminating the complexity of managing separate security tools.</p>
          </div>
          <div className="sc-architecture__layers">
            {archRows.map((row, ri) => (
              <div key={row.label}>
                {ri > 0 && <div className="sc-arch-row__connector">↕ Synchronized Security Intelligence ↕</div>}
                <div className="sc-arch-row">
                  <div className="sc-arch-row__label">{row.label}</div>
                  <div className="sc-arch-row__cards">
                    {row.cards.map(card => (
                      <div key={card.name} className="sc-arch-card sc-glass-panel">
                        <span className="sc-arch-card__icon">{card.icon}</span>
                        <div className="sc-arch-card__name">{card.name}</div>
                        <p className="sc-arch-card__desc">{card.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"3rem" }}>
            <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
              Design My Sophos Architecture <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ── Zero Trust ── */}
      <section className="sc-zerotrust">
        <div className="sc-shell">
          <div className="sc-zerotrust__inner">
            <img
              src="https://images.unsplash.com/photo-1639762681485-074b7f938ba0?auto=format&fit=crop&w=900&q=80"
              alt="Sophos Zero Trust security"
              className="sc-zerotrust__img"
            />
            <div>
              <div className="sc-label sc-label--teal"><span className="sc-dot" /> Zero Trust</div>
              <h2 className="sc-title">Never Trust. Always Verify. Continuously Monitor.</h2>
              <p className="sc-zerotrust__body">
                Sophos Zero Trust Network Access (ZTNA) replaces traditional VPN with application-level access control — granting users access to specific applications based on continuous identity and device verification rather than broad network access granted at connection time. When a device's security posture degrades, access is automatically revoked without manual intervention.
              </p>
              <p className="sc-zerotrust__body">
                ALG Data Guard designs Sophos ZTNA deployments integrated with your identity provider (Azure AD, Okta, Google Workspace) and Sophos Intercept X endpoint agent — creating a complete zero trust architecture that improves security while simplifying remote access for users.
              </p>
              <div className="sc-zerotrust__principles">
                {ztPrinciples.map(p => (
                  <div key={p.title} className="sc-zt-principle">
                    <span className="sc-zt-principle__icon">{p.icon}</span>
                    <div>
                      <div className="sc-zt-principle__title">{p.title}</div>
                      <p className="sc-zt-principle__desc">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
                  Replace My VPN with ZTNA <ArrowIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Endpoint ── */}
      <section className="sc-endpoint">
        <div className="sc-shell">
          <div className="sc-endpoint__inner">
            <div>
              <div className="sc-label sc-label--blue"><span className="sc-dot" /> Endpoint Protection</div>
              <h2 className="sc-title">Intercept X — Endpoint Security That Thinks Ahead</h2>
              <p className="sc-endpoint__body">
                Sophos Intercept X is consistently top-ranked by MITRE ATT&CK evaluations and AV-Comparatives for endpoint protection and EDR capability — combining deep learning anti-malware, behavioral detection, exploit prevention, and synchronized security into a single agent with the lowest performance impact in its class.
              </p>
              <p className="sc-endpoint__body">
                Every Intercept X deployment includes CryptoGuard ransomware rollback, Sophos's Synchronized Security heartbeat connection to XGS Firewall, and centralized policy management through Sophos Central — with optional XDR for organizations wanting investigation and hunting capabilities.
              </p>
              <div className="sc-endpoint__stack">
                {endpointStack.map(item => (
                  <div key={item.title} className="sc-ep-item">
                    <span className="sc-ep-item__icon">{item.icon}</span>
                    <div>
                      <div className="sc-ep-item__title">{item.title}</div>
                      <p className="sc-ep-item__desc">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop:"2rem" }}>
                <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
                  Deploy Intercept X <ArrowIcon />
                </button>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=900&q=80"
              alt="Sophos endpoint protection"
              className="sc-endpoint__img"
            />
          </div>
        </div>
      </section>

      {/* ── MDR ── */}
      <section className="sc-mdr">
        <div className="sc-shell">
          <div className="sc-mdr__head">
            <div className="sc-label sc-label--teal"><span className="sc-dot" /> Managed Detection & Response</div>
            <h2 className="sc-title">Sophos MDR — Expert Analysts. 24/7 Protection.</h2>
            <p className="sc-sub" style={{ margin:"0 auto", maxWidth:"620px" }}>Sophos MDR is the industry's most widely adopted MDR service — providing 24/7 threat hunting, detection, investigation, and response from Sophos's global team of security operations experts. Ranked as a Leader in Gartner's MDR Market Guide.</p>
          </div>
          <div className="sc-mdr__grid">
            {mdrCards.map(card => (
              <div key={card.title} className="sc-mdr-card sc-glass-panel">
                <span className="sc-mdr-card__icon">{card.icon}</span>
                <h3 className="sc-mdr-card__title">{card.title}</h3>
                <p className="sc-mdr-card__desc">{card.desc}</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:"3rem" }}>
            <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
              Start Sophos MDR <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* ── AI Security ── */}
      <section className="sc-ai">
        <div className="sc-shell">
          <div className="sc-ai__inner">
            <div>
              <div className="sc-label sc-label--teal"><span className="sc-dot" /> AI Security</div>
              <h2 className="sc-title">SophosAI — Threat Intelligence at Scale</h2>
              <p className="sc-ai__body">
                SophosAI is the intelligence layer powering every Sophos product — a suite of machine learning models trained on data from 500M+ protected devices globally. The scale of Sophos's threat intelligence network means every new attack technique discovered anywhere improves detection for every Sophos customer simultaneously.
              </p>
              <p className="sc-ai__body">
                SophosLabs analyzes 400,000+ unique malware samples daily — feeding intelligence into detection models that adapt to new threat techniques faster than traditional signature-based approaches can respond. This is why Sophos consistently achieves 99%+ detection rates in independent testing against the latest threat families.
              </p>
              <div className="sc-ai__features">
                {aiFeatures.map(f => (
                  <div key={f.title} className="sc-ai-feat">
                    <span className="sc-ai-feat__icon">{f.icon}</span>
                    <div>
                      <div className="sc-ai-feat__title">{f.title}</div>
                      <p className="sc-ai-feat__desc">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=900&q=80"
              alt="Sophos AI-powered security analytics"
              className="sc-ai__img"
            />
          </div>
        </div>
      </section>

      {/* ── Compliance ── */}
      <section className="sc-compliance">
        <div className="sc-shell">
          <div className="sc-compliance__head">
            <div className="sc-label sc-label--blue"><span className="sc-dot" /> Compliance</div>
            <h2 className="sc-title">Supporting Every Regulatory Framework</h2>
            <p className="sc-sub" style={{ margin:"0 auto", maxWidth:"580px" }}>Sophos Central's unified security platform provides technical controls mapped to every major compliance framework — with built-in reporting reducing audit burden.</p>
          </div>
          <div className="sc-compliance__frameworks">
            {complianceFrameworks.map(f => (
              <div key={f.name} className="sc-framework sc-glass-panel">
                <span className="sc-framework__icon">{f.icon}</span>
                <div className="sc-framework__name">{f.name}</div>
                <p className="sc-framework__desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industries ── */}
      <section className="sc-industries">
        <div className="sc-shell">
          <div className="sc-industries__head">
            <div className="sc-label sc-label--teal"><span className="sc-dot" /> Industries</div>
            <h2 className="sc-title">Cloud Security for Every Sector</h2>
            <p className="sc-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Sophos serves organizations in every regulated industry — with compliance-specific configurations, industry threat intelligence, and validated security architectures for every vertical.</p>
          </div>
          <div className="sc-industries__grid">
            {industries.map(ind => (
              <div key={ind.name} className="sc-ind-card">
                <span className="sc-ind-card__icon">{ind.icon}</span>
                <h3 className="sc-ind-card__name">{ind.name}</h3>
                <p className="sc-ind-card__desc">{ind.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Metrics ── */}
      <section className="sc-metrics" ref={metricsRef}>
        <div className="sc-shell">
          <div className="sc-metrics__inner">
            <div><div className="sc-metric__val">{c1}M+</div><div className="sc-metric__label">Endpoints Protected</div></div>
            <div><div className="sc-metric__val">{c2}K+</div><div className="sc-metric__label">Enterprise Customers</div></div>
            <div><div className="sc-metric__val">{c3}+</div><div className="sc-metric__label">Countries Served</div></div>
            <div><div className="sc-metric__val">{c4}/7</div><div className="sc-metric__label">MDR SOC Coverage</div></div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="sc-faq">
        <div className="sc-shell">
          <div className="sc-faq__head">
            <div className="sc-label sc-label--blue" style={{ margin:"0 auto 1rem" }}><span className="sc-dot" /> FAQs</div>
            <h2 className="sc-title">Sophos Questions, Answered</h2>
            <p className="sc-sub" style={{ margin:"0 auto", maxWidth:"560px" }}>Expert answers from ALG Data Guard's Sophos-certified security engineers about Intercept X, MDR, ZTNA, and Synchronized Security.</p>
          </div>
          <div className="sc-faq__list">
            {faqData.map((faq, i) => (
              <div key={i} className={`sc-faq-item${openFaq === i ? " open" : ""}`}>
                <button className="sc-faq-item__trigger" onClick={() => setOpenFaq(openFaq === i ? null : i)} aria-expanded={openFaq === i}>
                  <span className="sc-faq-item__q">{faq.q}</span>
                  <span className="sc-faq-item__arrow"><ChevronDown /></span>
                </button>
                <div className="sc-faq-item__answer"><div className="sc-faq-item__answer-inner">{faq.a}</div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="sc-cta">
        <div className="sc-cta__inner">
          <div className="sc-label sc-label--teal" style={{ margin:"0 auto 1.2rem" }}><span className="sc-dot" /> Get Protected</div>
          <h2 className="sc-cta__title">
            Security That's <em>Always On.</em><br />Always Learning.
          </h2>
          <p className="sc-cta__sub">Whether you need to deploy Sophos Central across your organization, extend protection with Zero Trust, add MDR coverage, or modernize your firewall infrastructure — ALG Data Guard's certified Sophos engineers design and deliver cloud security built for your threat environment.</p>
          <div className="sc-cta__actions">
            <button className="sc-btn sc-btn--blue" onClick={() => navigate("/contact")}>
              Deploy Sophos Now <ArrowIcon />
            </button>
            <button className="sc-btn sc-btn--teal" onClick={() => navigate("/contact")}>
              Free Security Assessment
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
